import { Component, Prop, Event, EventEmitter, h } from '@stencil/core';

export enum ButtonTheme {
  primary = 'primary',
  secondary = 'secondary',
  flat = 'flat',
}

export enum ButtonSize {
  s = 's',
  m = 'm',
  l = 'l',
}

@Component({
  tag: 'amp-button',
  styleUrls: ['../../base.css', 'button.css'],
  shadow: true,
})
export class AmpButton {
  /**
   * Theme: primary (default), secondary, flat
   */
  @Prop() type: ButtonTheme;
  /**
   * Size: s, m (default), l
   */
  @Prop() size: ButtonSize;
  /**
   * Sets a disabled state to the button
   */
  @Prop() disabled: boolean;
  /**
   * Sets a loading state to the button
   */
  @Prop() loading: boolean;
  /**
   * Allows the button to grow to 100% width of the parent container
   */
  @Prop() fullwidth: boolean;
  /**
   * Click event emitted when the button is clicked
   */
  @Event() buttonClicked: EventEmitter<void>;

  handleClick() {
    if (!this.disabled && !this.loading) {
      this.buttonClicked.emit();
    }
  }

  render() {
    const type = this.type || ButtonTheme.primary;
    const size = this.size || ButtonSize.m;
    const loading = this.loading || false;
    const disabled = loading || this.disabled || false;
    const fullWidth = this.fullwidth || false;

    const loadingIcon = loading && (
      <span
        class="fg-icon-loader absolute"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19 19">
          <symbol id="loader-ferg" class="loader loader-ferg" viewBox="0 0 62 62">
            <g fill="currentColor" fill-rule="evenodd" class="loader-logo">
              <g class="curve curve-1">
                <path id="Fill-12" d="M15.681 35.183H.293s-2.71-14.609 8.355-24.81c0 0 7.004-9.338 22.135-9.42v15.3s-7.002-.262-11.414 5.101c0 0-5.302 4.323-3.688 13.829"></path>
              </g>
              <g class="curve curve-2">
                <path id="Fill-10" d="M46.254 1.124h15.217s1.554 12.191-9.944 22.564c0 0-8.298 8.3-20.744 7.781V16.256s6.394.344 10.978-4.667c0 0 4.577-4.071 4.493-10.465"></path>
              </g>
              <g class="curve curve-3">
                <path id="Fill-9" d="M45.88 27.749h15.388s2.709 14.61-8.359 24.816c0 0-7.002 9.336-22.126 9.42V46.684s7.002.264 11.411-5.103c0 0 5.299-4.322 3.686-13.832"></path>
              </g>
              <g class="curve curve-4">
                <path
                  id="Fill-13"
                  d="M15.305 61.81H.089s-1.555-12.187 9.946-22.561c0 0 8.296-8.302 20.748-7.781V46.68s-6.401-.343-10.981 4.668c0 0-4.579 4.064-4.497 10.462"
                ></path>
              </g>
            </g>
            <g class="loader-circle">
              <circle fill="transparent" cx="31" cy="31" r="22.5" stroke="currentColor" stroke-width="24.5%"></circle>
            </g>
          </symbol>
          <use xlinkHref="#loader-ferg"></use>
        </svg>
      </span>
    );

    let className = '';

    switch (type) {
      case ButtonTheme.secondary:
        className += 'inline-flex items-center relative secondary-button text-primary font-semibold button-border button-hover button-active secondary-button-disabled ';
        break;
      case ButtonTheme.flat:
        className += 'inline-flex items-center relative bg-transparent text-primary font-semibold text-button-border bg-secondary-hover text-button-active text-button-disabled ';
        break;
      default:
        className += 'inline-flex items-center relative primary-button text-white font-semibold button-border button-hover button-active primary-button-disabled ';
    }

    switch (size) {
      case ButtonSize.s:
        className += 'py-2 px-4';
        break;
      case ButtonSize.l:
        className += 'py-4 px-8';
        break;
      default:
        className += 'py-3 px-6';
    }

    if (loading) {
      className += ' loading-button';
    }

    if (fullWidth) {
      className += ' w-full justify-center';
    }

    return (
      <button class={className} disabled={disabled} onClick={this.handleClick.bind(this)}>
        {loadingIcon}
        <span class="button-text">
          <slot />
        </span>
      </button>
    );
  }
}
